// react core
import { useContext, useState } from "react";

// date handling
import dayjs from "dayjs";

// material design
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Grow from "@mui/material/Grow";
import Drawer from "@mui/material/Drawer";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";

// entzy context and services
import configEntzy from "components/config/ConfigEntzy";
import { MainContext } from "components/main/MainContext";
import { EventContext } from "components/event/EventContext";
import {
  DateTickingHeader,
  DateTickingProgressBar,
  DateTickingBreakdown,
} from "components/event/lists/ListDates";
import { ActionBoxButton } from "components/utils/common/CommonButtons";
import { FallbackMessage } from "components/event/lists/ListFallbacks";

// entzy components
import RoomDatesTicking from "./RoomDatesTicking";
import ActionCore from "components/event/actions/ActionCore";

// fonts and colors
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft as iconPrev } from "@fortawesome/pro-solid-svg-icons";
import { faChevronRight as iconNext } from "@fortawesome/pro-solid-svg-icons";
import { faCalendarCircleMinus as iconNoActivity } from "@fortawesome/pro-duotone-svg-icons";
import { faClose as iconClose } from "@fortawesome/pro-thin-svg-icons";

export const RoomDatesChartItemDetail = (props) => {
  const user = props.user;
  const date = props.date ? props.date : {};
  const expandDateDetail = props.expandDateDetail;
  const handleExpandDateDetail = props.handleExpandDateDetail;
  const handleDateSelectNext = props.handleDateSelectNext;
  const handleDateSelectPrev = props.handleDateSelectPrev;

  const eventContext = useContext(EventContext);
  // const mainContext = useContext(MainContext);

  // check if before yesterday
  const dateJs = dayjs(date.id, "YYYY-MM-DD");
  const isDateOfferable =
    date &&
    dateJs.isAfter(
      dayjs().add(eventContext.state.event.calendar.limits.notice, "days"),
    );
  const isDateLaunched = date && date.isDatePostLaunch;
  const isDateLaunchable =
    date && isDateOfferable && date.isRunnerLaunchable && !date.isRunnerOffer;

  // enrich date with any validator flags
  date.isStartMonth = dateJs.isSame(dateJs.startOf("month"), "day");
  date.isEndMonth = dateJs.isSame(dateJs.endOf("month"), "day");

  // if (date.id === expandDateDetail) {
  //   console.log("date", date);
  // }

  return (
    <Box className="box-default" hidden={!date}>
      <Drawer
        anchor="bottom"
        open={date.id === expandDateDetail}
        onClose={handleExpandDateDetail}
        ModalProps={{ disableScrollLock: true }}
        keepMounted={true}
      >
        <Box
          className="box-default bg-black-t90"
          key={date.id}
          sx={{
            p: configEntzy.APP_SPACING_MD2X,
          }}
        >
          <Grow in={date.id === expandDateDetail} timeout={200}>
            <Container maxWidth="md" disableGutters>
              <Box
                className="box-default"
                sx={{
                  pb: configEntzy.APP_SPACING_MD2X,
                }}
              >
                <Box className="box-default quarter-width">
                  <Typography variant="h6">
                    <span>&nbsp;</span>
                  </Typography>
                </Box>
                <Box className="box-default half-width">
                  <Typography variant="h6" color="orange">
                    <span>{date.dow}</span>
                    <span>&nbsp;</span>
                    <span>{date.day}</span>
                    <span>&nbsp;</span>
                    <span>{date.month}</span>
                    <span>&nbsp;</span>
                    <span>{date.year}</span>
                  </Typography>
                  <Typography variant="subtitle2" color="white">
                    Live Date Activity
                  </Typography>
                </Box>
                <Box
                  className="box-default quarter-width text-right action-pointer"
                  onClick={handleExpandDateDetail}
                >
                  <Typography variant="h6">
                    <FontAwesomeIcon icon={iconClose} transform="grow-4" />
                  </Typography>
                </Box>
              </Box>
              <Box
                className="box-default"
                sx={{
                  mb: configEntzy.APP_SPACING_MD2X,
                }}
              >
                {date.ticking ? (
                  <RoomDatesTicking key={date.id} {...props} viewDate={date} />
                ) : (
                  <Box
                    className="box-default bg-black-t50 shadow-dark"
                    sx={{
                      pt: configEntzy.APP_SPACING_HL,
                      pb: configEntzy.APP_SPACING_HL,
                      mb: configEntzy.APP_SPACING_MD,
                      borderRadius: configEntzy.BORDER_SIZE_XL,
                    }}
                  >
                    {isDateOfferable ? (
                      <Box className="box-default">
                        <Typography variant="subtitle2">
                          <FontAwesomeIcon
                            icon={iconNoActivity}
                            size="3x"
                            transform="up-4"
                          />
                        </Typography>
                        <Typography variant="subtitle2">
                          No date activity yet
                        </Typography>
                        <Typography variant="subtitle2">
                          Place an offer to get things started
                        </Typography>
                      </Box>
                    ) : (
                      <Box className="box-default">
                        <Typography variant="subtitle2">
                          This date is expired or past notice
                        </Typography>
                        <Typography variant="subtitle2">
                          Check out other dates to make an offer
                        </Typography>
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
              {/* ----- matched and overrides navigation ----- */}
              <Box className="box-default">
                {user.connected &&
                  eventContext.state.event.manager &&
                  isDateOfferable &&
                  !isDateLaunched &&
                  !isDateLaunchable && (
                    <Box
                      className="box-default"
                      sx={{
                        mb: configEntzy.APP_SPACING_MD2X,
                        borderRadius: configEntzy.BORDER_SIZE_XL,
                      }}
                    >
                      <Box
                        className="box-default shadow-purple"
                        sx={{
                          p: configEntzy.APP_SPACING_XS,
                          borderRadius: configEntzy.BORDER_SIZE_XL,
                        }}
                      >
                        <ActionCore
                          {...props}
                          room="dates"
                          actionText="Launch Override"
                          actionParams={{
                            dateKey: date.id,
                            launchOverride: true,
                          }}
                          mini={true}
                          disableGlow={true}
                          color="night"
                          fullWidth={true}
                        />
                      </Box>
                    </Box>
                  )}
                <Box
                  className="box-default"
                  sx={{
                    mb: configEntzy.APP_SPACING_MD2X,
                    borderRadius: configEntzy.BORDER_SIZE_XL,
                  }}
                >
                  {isDateOfferable ? (
                    <Box className="box-default">
                      <ActionCore
                        {...props}
                        room="dates"
                        actionText="New Date Offer"
                        actionParams={{
                          dateKey: date.id,
                        }}
                        // icon={iconOffer}
                        mini={true}
                        fullWidth={true}
                      />
                    </Box>
                  ) : (
                    <Box className="box-default">
                      <ActionBoxButton
                        size="small"
                        text="View Matched Dates"
                        bgColor="primary.main"
                        color="black"
                        rounded={true}
                        onClick={() => {
                          eventContext.focusModule("entry");
                        }}
                      />
                    </Box>
                  )}
                </Box>
              </Box>
              {/* ----- prev and next navigation ----- */}
              <Box className="box-default">
                <Box
                  className="box-default half-width"
                  sx={{ pr: configEntzy.APP_SPACING_XS2X }}
                >
                  <ActionBoxButton
                    size="small"
                    text="Prev Day"
                    bgColor="black"
                    color="white"
                    borderColor="orange"
                    rounded={true}
                    disabled={date.isStartMonth}
                    onClick={() => {
                      handleDateSelectPrev(date);
                    }}
                    startIcon={<FontAwesomeIcon icon={iconPrev} />}
                  />
                </Box>
                <Box
                  className="box-default half-width"
                  sx={{ pl: configEntzy.APP_SPACING_XS2X }}
                >
                  <ActionBoxButton
                    size="small"
                    text="Next Day"
                    bgColor="black"
                    color="white"
                    borderColor="orange"
                    rounded={true}
                    disabled={date.isEndMonth}
                    onClick={() => {
                      handleDateSelectNext(date);
                    }}
                    endIcon={<FontAwesomeIcon icon={iconNext} />}
                  />
                </Box>
              </Box>
            </Container>
          </Grow>
        </Box>
      </Drawer>
    </Box>
  );
};

export const RoomDatesChartItem = (props) => {
  const date = props.item;
  const eventView = props.eventView;
  const eventContext = useContext(EventContext);
  const mainContext = useContext(MainContext);

  const [expandDate, setExpandDate] = useState(eventView ? true : false);
  const [expandDateDetail, setExpandDateDetail] = useState(null);

  const handleExpandDate = () => {
    setExpandDate(!expandDate);
  };
  const handleExpandDateDetail = () => {
    setExpandDateDetail(expandDateDetail === date.id ? null : date.id);
  };

  return (
    <Box
      key={date.id}
      className={
        "box-default nooverflow" + (expandDate ? "" : " action-pointover")
      }
      sx={{
        height: expandDate ? "auto" : configEntzy.AVATAR_CONTAINER_SM,
        borderBottom: expandDate ? "none" : "2px solid #555",
        // pl: configEntzy.APP_SPACING_MD,
        // pr: configEntzy.APP_SPACING_MD,
        mb: configEntzy.APP_SPACING_MD,
      }}
    >
      <Box className="box-default">
        <Box
          className={
            "box-default " +
            (eventContext.state.event.calendar.dates.launched.all.keys.includes(
              date.id,
            )
              ? "shadow-default bg-highlight"
              : date.isExpired
                ? "bg-black-t10"
                : "bg-black-t50")
          }
          sx={{
            p: configEntzy.APP_SPACING_MD,
            mb: configEntzy.APP_SPACING_MD,
            borderRadius: configEntzy.BORDER_SIZE_XL,
          }}
        >
          <Grid container spacing={0}>
            <Grid
              item
              xs={6}
              sx={{
                borderRight: "1px solid #333",
              }}
            >
              <Box
                className="box-default action-pointer"
                onClick={handleExpandDate}
              >
                <DateTickingHeader
                  date={date}
                  runners={true}
                  triggers={eventContext.state.event.calendar.triggers}
                  limits={eventContext.state.event.calendar.limits}
                  launched={eventContext.state.event.calendar.dates.launched.all.keys.includes(
                    date.id,
                  )}
                  launchPerTicket={
                    eventContext.state.event.data.LaunchPerTicket
                  }
                  eventView={eventView}
                />
              </Box>
              <Box
                className="box-default action-pointer"
                onClick={handleExpandDateDetail}
              >
                <DateTickingProgressBar
                  date={date}
                  runners={true}
                  triggers={eventContext.state.event.calendar.triggers}
                  limits={eventContext.state.event.calendar.limits}
                  icons={{ trigger: props.module.icon.trigger }}
                  launched={eventContext.state.event.calendar.dates.launched.all.keys.includes(
                    date.id,
                  )}
                  launchPerTicket={
                    eventContext.state.event.data.LaunchPerTicket
                  }
                  eventView={eventView}
                />
                {eventView &&
                mainContext.state.viewer.trail.calendarCompactView ? (
                  <></>
                ) : (
                  <DateTickingBreakdown
                    date={date}
                    runners={true}
                    triggers={eventContext.state.event.calendar.triggers}
                    limits={eventContext.state.event.calendar.limits}
                    launched={eventContext.state.event.calendar.dates.launched.all.keys.includes(
                      date.id,
                    )}
                    launchPerTicket={
                      eventContext.state.event.data.LaunchPerTicket
                    }
                    eventView={eventView}
                  />
                )}
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box
                className="box-default action-pointer"
                onClick={handleExpandDate}
              >
                <DateTickingHeader
                  date={date}
                  runners={false}
                  triggers={eventContext.state.event.calendar.triggers}
                  limits={eventContext.state.event.calendar.limits}
                  launched={eventContext.state.event.calendar.dates.launched.all.keys.includes(
                    date.id,
                  )}
                  launchPerTicket={
                    eventContext.state.event.data.LaunchPerTicket
                  }
                  eventView={eventView}
                />
              </Box>
              <Box
                className="box-default action-pointer"
                onClick={handleExpandDateDetail}
              >
                <DateTickingProgressBar
                  date={date}
                  runners={false}
                  triggers={eventContext.state.event.calendar.triggers}
                  limits={eventContext.state.event.calendar.limits}
                  icons={{ trigger: props.module.icon.trigger }}
                  launched={eventContext.state.event.calendar.dates.launched.all.keys.includes(
                    date.id,
                  )}
                  launchPerTicket={
                    eventContext.state.event.data.LaunchPerTicket
                  }
                  eventView={eventView}
                />
                {eventView &&
                mainContext.state.viewer.trail.calendarCompactView ? (
                  <></>
                ) : (
                  <DateTickingBreakdown
                    date={date}
                    runners={false}
                    triggers={eventContext.state.event.calendar.triggers}
                    limits={eventContext.state.event.calendar.limits}
                    launched={eventContext.state.event.calendar.dates.launched.all.keys.includes(
                      date.id,
                    )}
                    launchPerTicket={
                      eventContext.state.event.data.LaunchPerTicket
                    }
                    eventView={eventView}
                  />
                )}
              </Box>
            </Grid>
          </Grid>

          <RoomDatesChartItemDetail
            {...props}
            date={date}
            expandDateDetail={expandDateDetail}
            handleExpandDateDetail={handleExpandDateDetail}
          />
        </Box>
      </Box>
    </Box>
  );
};

function RoomDatesChart(props) {
  const eventContext = useContext(EventContext);

  return (
    <Box id="anchor-room-dates-chart" className="box-default">
      {!eventContext.state.event.data.Active && (
        <Box
          className="box-default"
          sx={{
            p: configEntzy.APP_SPACING_LG,
          }}
        >
          <Typography variant="subtitle2">Offer Exchange Closed</Typography>
          <Typography variant="subtitle1">
            <em>Eventuator is Inactive</em>
          </Typography>
        </Box>
      )}

      <Box
        className="box-default"
        sx={{
          textAlign: "left",
          pt: configEntzy.APP_SPACING_MD,
        }}
      >
        {eventContext.state.event.calendar.dates.combined.active.length > 0 ? (
          <Box className="box-default">
            {eventContext.state.event.calendar.dates.combined.active.map(
              (item) => {
                return (
                  <RoomDatesChartItem key={item.id} {...props} item={item} />
                );
              },
            )}
          </Box>
        ) : (
          <FallbackMessage
            fallback={props.module.fallback}
            room={true}
            split={true}
          />
        )}
      </Box>
    </Box>
  );
}

export default RoomDatesChart;
